import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GetCourseVersionHistoryDocument,
  ModernizedActivityStatus,
  useGetCourseSnapshotReviewLazyQuery,
  useRestoreCourseMutation,
} from "@axio/graphql/generated/graphql";

import type { SnapshotVersionEntry } from "../components/VersionHistorySheet";
import {
  buildHistoricalSnapshotDisplayState,
  type HistoricalSnapshotDisplayState,
} from "../historicalSnapshotDisplayState";
import type {
  ActivityDetail,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewSidebarItem,
} from "../types/reviewTypes";

type ShowToast = (toast: { message: string; type: "success" | "error" }) => void;

interface UseHistoricalSnapshotReviewParams {
  courseId: string;
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  liveReview: CourseEvaluatorReviewData;
  liveActivityDetails: Record<string, ActivityDetail>;
  liveSelectedSidebarItem: CourseEvaluatorReviewSidebarItem | null;
  liveReviewedProgress: number;
  selectedItemId: string | null;
  filterMode: "all" | "undecided";
  getLiveDecision: (id: string) => CourseEvaluatorReviewDecision;
  onRefreshReview: () => Promise<unknown>;
  showToast: ShowToast;
  onBeforeViewSnapshot?: () => void;
  onRestoreApplied?: () => void;
}

function getDecisionFromStatus(status: ModernizedActivityStatus | null | undefined): CourseEvaluatorReviewDecision {
  switch (status) {
    case ModernizedActivityStatus.Queued:
      return "accepted";
    case ModernizedActivityStatus.Skipped:
      return "rejected";
    case ModernizedActivityStatus.Published:
      return "synced";
    default:
      return null;
  }
}

export function useHistoricalSnapshotReview({
  courseId,
  review,
  activityDetails,
  liveReview,
  liveActivityDetails,
  liveSelectedSidebarItem,
  liveReviewedProgress,
  selectedItemId,
  filterMode,
  getLiveDecision,
  onRefreshReview,
  showToast,
  onBeforeViewSnapshot,
  onRestoreApplied,
}: UseHistoricalSnapshotReviewParams) {
  const [fetchCourseSnapshotReview, { loading: isFetchingCourseSnapshotReview }] = useGetCourseSnapshotReviewLazyQuery({
    fetchPolicy: "network-only",
  });
  const [restoreCourseMutation, { loading: isRestoreCourseMutationLoading }] = useRestoreCourseMutation();
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [viewingSnapshot, setViewingSnapshot] = useState<SnapshotVersionEntry | null>(null);
  const [historicalSnapshotDisplayState, setHistoricalSnapshotDisplayState] =
    useState<HistoricalSnapshotDisplayState | null>(null);
  const [isRestoreSnapshotModalOpen, setIsRestoreSnapshotModalOpen] = useState(false);
  const [pendingRestoreSnapshotId, setPendingRestoreSnapshotId] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const snapshotReviewRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const activeHistoricalSnapshotDisplayState =
    historicalSnapshotDisplayState && viewingSnapshot?.id === historicalSnapshotDisplayState.snapshot.id
      ? historicalSnapshotDisplayState
      : null;
  const historicalDecisionMap = useMemo(() => {
    if (!activeHistoricalSnapshotDisplayState) {
      return null;
    }

    return new Map(
      activeHistoricalSnapshotDisplayState.review.sidebarGroups.flatMap((group) =>
        group.items.map((item) => [item.id, getDecisionFromStatus(item.status)] as const)
      )
    );
  }, [activeHistoricalSnapshotDisplayState]);
  const displayReview = useMemo<CourseEvaluatorReviewData>(() => {
    if (!activeHistoricalSnapshotDisplayState || !historicalDecisionMap) {
      return liveReview;
    }

    if (filterMode === "all") {
      return activeHistoricalSnapshotDisplayState.review;
    }

    return {
      ...activeHistoricalSnapshotDisplayState.review,
      sidebarGroups: activeHistoricalSnapshotDisplayState.review.sidebarGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => historicalDecisionMap.get(item.id) === null),
        }))
        .filter((group) => group.items.length > 0),
    };
  }, [activeHistoricalSnapshotDisplayState, filterMode, historicalDecisionMap, liveReview]);
  const displayActivityDetails = activeHistoricalSnapshotDisplayState?.activityDetails ?? liveActivityDetails;
  const historicalSelectedSidebarItem = useMemo<CourseEvaluatorReviewSidebarItem | null>(() => {
    if (!selectedItemId) return null;
    return (
      displayReview.sidebarGroups.flatMap((group) => group.items).find((item) => item.id === selectedItemId) ?? null
    );
  }, [displayReview.sidebarGroups, selectedItemId]);
  const displaySelectedSidebarItem = activeHistoricalSnapshotDisplayState
    ? historicalSelectedSidebarItem
    : liveSelectedSidebarItem;
  const displayGetDecision = useCallback(
    (id: string): CourseEvaluatorReviewDecision =>
      historicalDecisionMap ? (historicalDecisionMap.get(id) ?? null) : getLiveDecision(id),
    [getLiveDecision, historicalDecisionMap]
  );
  const displaySelectedDecision = selectedItemId ? displayGetDecision(selectedItemId) : null;
  const displayReviewedProgress = activeHistoricalSnapshotDisplayState
    ? displayReview.totalCount > 0
      ? (displayReview.reviewedCount / displayReview.totalCount) * 100
      : 0
    : liveReviewedProgress;

  const handleOpenVersionHistory = useCallback(() => {
    setIsVersionHistoryOpen(true);
  }, []);

  const handleCloseVersionHistory = useCallback(() => {
    setIsVersionHistoryOpen(false);
  }, []);

  const handleViewSnapshot = useCallback(
    async (snapshot: SnapshotVersionEntry) => {
      const requestId = snapshotReviewRequestIdRef.current + 1;
      snapshotReviewRequestIdRef.current = requestId;

      onBeforeViewSnapshot?.();
      setIsVersionHistoryOpen(false);
      setIsRestoreSnapshotModalOpen(false);
      setViewingSnapshot(snapshot);
      setHistoricalSnapshotDisplayState(null);

      try {
        const result = await fetchCourseSnapshotReview({
          variables: {
            courseId,
            snapshotId: snapshot.id,
          },
        });
        const payload = result.data?.getCourseSnapshotReview;
        if (!payload?.success || !payload.data) {
          throw new Error(payload?.message ?? "Snapshot could not be loaded");
        }

        const loadedSnapshot = payload.snapshot ? { ...snapshot, ...payload.snapshot } : snapshot;
        if (!isMountedRef.current || snapshotReviewRequestIdRef.current !== requestId) return;

        setViewingSnapshot(loadedSnapshot);
        setHistoricalSnapshotDisplayState(
          buildHistoricalSnapshotDisplayState({
            snapshot: loadedSnapshot,
            review,
            activityDetails,
            snapshotData: payload.data,
          })
        );
      } catch {
        if (!isMountedRef.current || snapshotReviewRequestIdRef.current !== requestId) return;

        setViewingSnapshot(null);
        setHistoricalSnapshotDisplayState(null);
        showToast({
          message: "Could not load that snapshot. Please try again.",
          type: "error",
        });
      }
    },
    [activityDetails, courseId, fetchCourseSnapshotReview, onBeforeViewSnapshot, review, showToast]
  );

  const handleBackToCurrentSnapshot = useCallback(() => {
    if (pendingRestoreSnapshotId !== null || isRestoreCourseMutationLoading) {
      return;
    }

    snapshotReviewRequestIdRef.current += 1;
    setViewingSnapshot(null);
    setHistoricalSnapshotDisplayState(null);
    setIsRestoreSnapshotModalOpen(false);
  }, [isRestoreCourseMutationLoading, pendingRestoreSnapshotId]);

  const handleOpenRestoreSnapshotConfirmation = useCallback(() => {
    if (!viewingSnapshot || pendingRestoreSnapshotId !== null || isRestoreCourseMutationLoading) {
      return;
    }

    setIsRestoreSnapshotModalOpen(true);
  }, [isRestoreCourseMutationLoading, pendingRestoreSnapshotId, viewingSnapshot]);

  const handleCancelRestoreSnapshot = useCallback(() => {
    if (pendingRestoreSnapshotId !== null || isRestoreCourseMutationLoading) {
      return;
    }

    setIsRestoreSnapshotModalOpen(false);
  }, [isRestoreCourseMutationLoading, pendingRestoreSnapshotId]);

  const isRestoreSnapshotPending = pendingRestoreSnapshotId !== null || isRestoreCourseMutationLoading;

  const handleConfirmRestoreSnapshot = useCallback(async () => {
    if (!viewingSnapshot || isRestoreSnapshotPending) {
      return;
    }

    const snapshot = viewingSnapshot;
    setPendingRestoreSnapshotId(snapshot.id);

    try {
      const result = await restoreCourseMutation({
        variables: {
          courseId,
          snapshotId: snapshot.id,
        },
        refetchQueries: [
          {
            query: GetCourseVersionHistoryDocument,
            variables: { courseId },
          },
        ],
        awaitRefetchQueries: true,
      });
      const restoreResult = result.data?.restoreCourse;

      if (!restoreResult?.success) {
        if (isMountedRef.current) {
          showToast({
            message: restoreResult?.message ?? "Unable to restore this version.",
            type: "error",
          });
        }
        return;
      }

      const refreshResults = await Promise.allSettled([onRefreshReview()]);

      if (!isMountedRef.current) {
        return;
      }

      onRestoreApplied?.();
      snapshotReviewRequestIdRef.current += 1;
      setViewingSnapshot(null);
      setHistoricalSnapshotDisplayState(null);
      setIsRestoreSnapshotModalOpen(false);
      showToast({
        message: `Restored to v${snapshot.versionNumber}.`,
        type: "success",
      });

      if (refreshResults.some((refreshResult) => refreshResult.status === "rejected")) {
        showToast({
          message: "Restore completed, but the latest course data could not be refreshed. Refresh the page to see it.",
          type: "error",
        });
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      showToast({
        message: error instanceof Error ? error.message : "Unable to restore this version.",
        type: "error",
      });
    } finally {
      if (isMountedRef.current) {
        setPendingRestoreSnapshotId(null);
      }
    }
  }, [
    courseId,
    isRestoreSnapshotPending,
    onRefreshReview,
    onRestoreApplied,
    restoreCourseMutation,
    showToast,
    viewingSnapshot,
  ]);

  return {
    displayActivityDetails,
    displayGetDecision,
    displayReview,
    displayReviewedProgress,
    displaySelectedDecision,
    displaySelectedSidebarItem,
    isFetchingCourseSnapshotReview,
    isRestoreSnapshotModalOpen,
    isRestoreSnapshotPending,
    isVersionHistoryOpen,
    viewingSnapshot,
    handleBackToCurrentSnapshot,
    handleCancelRestoreSnapshot,
    handleCloseVersionHistory,
    handleConfirmRestoreSnapshot,
    handleOpenRestoreSnapshotConfirmation,
    handleOpenVersionHistory,
    handleViewSnapshot,
  };
}
